import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getCandidateProfile, updateCandidateProfile } from '../services/api';
import SearchableMultiSelect from '../components/SearchableMultiSelect';

const CandidateProfileEditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  
  const [resumeFile, setResumeFile] = useState(null);
  const [existingResume, setExistingResume] = useState(null); // { url, filename }

  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobRole: '',
    gender: '',
    dob: '',
    nationality: '',
    maritalStatus: '',
    city: '',

    // Skills & Languages
    skills: [],
    languages: [],

    // Education
    education: [],

    // Work Experience
    workExperience: [],

    // Projects
    projects: [],

    // Certifications
    certifications: [],
  });

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  const candidateEmail = session?.email;

  useEffect(() => {
    if (!candidateId) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [candidateId, navigate]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = await getCandidateProfile(candidateId, candidateEmail);
      
      if (profile) {
        setFormData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || candidateEmail,
          phone: profile.phone_number || profile.phone || '',
          jobRole: profile.job_role || '',
          gender: profile.gender || '',
          dob: profile.dob || profile.date_of_birth || '',
          nationality: profile.nationality || '',
          maritalStatus: profile.marital_status || '',
          city: profile.city || profile.location || '',
          skills: Array.isArray(profile.skills) ? profile.skills : [],
          languages: Array.isArray(profile.languages) ? profile.languages : [],
          education: Array.isArray(profile.education) ? profile.education : [],
          workExperience: Array.isArray(profile.work_experience) ? profile.work_experience : [],
          projects: Array.isArray(profile.projects) ? profile.projects : [],
          certifications: Array.isArray(profile.courses) ? profile.courses : [],
        });
        if (profile.resume_url || profile.resume_filename) {
          setExistingResume({
            url: profile.resume_url,
            filename: profile.resume_filename || 'Resume.pdf',
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayItemChange = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, template) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], template]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Create FormData to match backend API
      const profileData = new FormData();
      profileData.append('candidate_id', candidateId);
      profileData.append('full_name', `${formData.firstName} ${formData.lastName}`.trim());
      profileData.append('email', formData.email);
      profileData.append('phone', formData.phone);
      profileData.append('job_role', formData.jobRole);
      profileData.append('gender', formData.gender);
      profileData.append('dob', formData.dob);
      profileData.append('nationality', formData.nationality);
      profileData.append('marital_status', formData.maritalStatus);
      profileData.append('location', formData.city); // Use city as location
      profileData.append('skills', JSON.stringify(formData.skills));
      profileData.append('languages', JSON.stringify(formData.languages));
      profileData.append('education', JSON.stringify(formData.education));
      profileData.append('work_experience', JSON.stringify(formData.workExperience));
      profileData.append('projects', JSON.stringify(formData.projects));
      profileData.append('courses', JSON.stringify(formData.certifications));

      // Attach resume file if a new one was selected
      if (resumeFile) {
        profileData.append('resume', resumeFile);
      }

      // Use fetch API to send FormData
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE_URL}/candidates/profile`, {
        method: 'PUT',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: profileData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      alert('Profile updated successfully!');
      
      // Clear profile cache to force fresh data load
      SessionManager.clearProfileCache('candidate');
      
      navigate('/candidate/profile', { replace: true, state: { reload: true } });
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Failed to save profile: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
            <p className="text-gray-600 mt-1">Update your profile information</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/candidate/profile')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Resume Upload Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Icon name="description" size={20} className="text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Resume</h3>
              <p className="text-sm text-gray-500">Upload or update your resume (PDF only, max 5MB)</p>
            </div>
          </div>
          {resumeFile ? (
            <div className="flex items-center gap-3 p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <Icon name="description" size={24} className="text-teal-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{resumeFile.name}</p>
                <p className="text-xs text-gray-500">{(resumeFile.size / 1024 / 1024).toFixed(2)} MB - Ready to upload on save</p>
              </div>
              <button
                type="button"
                onClick={() => setResumeFile(null)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <Icon name="close" size={20} />
              </button>
            </div>
          ) : existingResume ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="check_circle" size={24} className="text-green-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{existingResume.filename}</p>
                <p className="text-xs text-green-600">Currently uploaded</p>
              </div>
              <label className="cursor-pointer px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                Replace Resume
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        alert('Resume must be less than 5MB');
                        return;
                      }
                      if (!file.name.toLowerCase().endsWith('.pdf')) {
                        alert('Only PDF files are allowed');
                        return;
                      }
                      setResumeFile(file);
                    }
                  }}
                />
              </label>
            </div>
          ) : (
            <label className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors">
              <Icon name="cloud_upload" size={32} className="text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Click to upload your resume</p>
                <p className="text-xs text-gray-500">PDF only, max 5MB</p>
              </div>
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      alert('Resume must be less than 5MB');
                      return;
                    }
                    if (!file.name.toLowerCase().endsWith('.pdf')) {
                      alert('Only PDF files are allowed');
                      return;
                    }
                    setResumeFile(file);
                  }
                }}
              />
            </label>
          )}
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: 'personal', label: 'Personal Info', icon: 'person' },
              { id: 'skills', label: 'Skills', icon: 'star' },
              { id: 'education', label: 'Education', icon: 'school' },
              { id: 'work', label: 'Work Experience', icon: 'work' },
              { id: 'projects', label: 'Projects', icon: 'code' },
              { id: 'certifications', label: 'Certifications', icon: 'workspace_premium' },
            ].map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeSection === section.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon name={section.icon} size={18} />
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Personal Info Section */}
          {activeSection === 'personal' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Role</label>
                  <input
                    type="text"
                    name="jobRole"
                    value={formData.jobRole}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City/Location</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

            </div>
          )}

          {/* Skills Section */}
          {activeSection === 'skills' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Skills & Languages</h2>
              
              <SearchableMultiSelect
                label="Skills"
                value={formData.skills}
                onChange={(selected) => setFormData(prev => ({ ...prev, skills: selected }))}
                placeholder="Search and select skills..."
                groupedOptions={[
                  {
                    label: "Programming Languages",
                    options: ["JavaScript", "TypeScript", "Python", "Java", "C++", "C#", "Go", "Rust", "PHP", "Ruby", "Swift", "Kotlin", "R", "MATLAB"]
                  },
                  {
                    label: "Frontend",
                    options: ["React", "Angular", "Vue.js", "HTML", "CSS", "SASS", "Tailwind CSS", "Bootstrap", "jQuery"]
                  },
                  {
                    label: "Backend",
                    options: ["Node.js", "Express.js", "Django", "Flask", "Spring Boot", "Laravel", "Ruby on Rails", "ASP.NET"]
                  },
                  {
                    label: "Databases",
                    options: ["SQL", "MySQL", "PostgreSQL", "MongoDB", "Redis", "Oracle", "SQLite", "Cassandra"]
                  },
                  {
                    label: "Cloud & DevOps",
                    options: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Jenkins", "CI/CD", "Terraform"]
                  },
                  {
                    label: "Data Science & AI",
                    options: ["Machine Learning", "Deep Learning", "TensorFlow", "PyTorch", "Pandas", "NumPy", "Scikit-learn", "Data Analysis"]
                  },
                  {
                    label: "Other",
                    options: ["Git", "GitHub", "REST API", "GraphQL", "Agile", "Scrum", "Project Management", "UI/UX Design"]
                  }
                ]}
              />

              <SearchableMultiSelect
                label="Languages"
                value={formData.languages || []}
                onChange={(selected) => setFormData(prev => ({ ...prev, languages: selected }))}
                placeholder="Search and select languages..."
                options={["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Bengali", "Marathi", "Gujarati", "Punjabi", "Spanish", "French", "German", "Chinese", "Japanese", "Korean", "Arabic", "Russian"]}
              />
            </div>
          )}

          {/* Education Section */}
          {activeSection === 'education' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Education</h2>
                <button
                  onClick={() => addArrayItem('education', {
                    institution: '',
                    degree: '',
                    field: '',
                    start_year: '',
                    end_year: '',
                    description: ''
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Icon name="add" size={18} />
                  Add Education
                </button>
              </div>

              {formData.education.map((edu, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-lg relative">
                  {formData.education.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('education', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Institution</label>
                      <select
                        value={edu.institution || ''}
                        onChange={(e) => handleArrayItemChange('education', index, 'institution', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select institution</option>
                        <option value="IIT Bombay">IIT Bombay</option>
                        <option value="IIT Delhi">IIT Delhi</option>
                        <option value="IIT Madras">IIT Madras</option>
                        <option value="IIT Kanpur">IIT Kanpur</option>
                        <option value="IIT Kharagpur">IIT Kharagpur</option>
                        <option value="NIT Trichy">NIT Trichy</option>
                        <option value="NIT Warangal">NIT Warangal</option>
                        <option value="BITS Pilani">BITS Pilani</option>
                        <option value="Anna University">Anna University</option>
                        <option value="Delhi University">Delhi University</option>
                        <option value="Mumbai University">Mumbai University</option>
                        <option value="VIT Vellore">VIT Vellore</option>
                        <option value="SRM University">SRM University</option>
                        <option value="Amity University">Amity University</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Degree</label>
                      <select
                        value={edu.degree || ''}
                        onChange={(e) => handleArrayItemChange('education', index, 'degree', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select degree</option>
                        <option value="B.Tech">B.Tech</option>
                        <option value="B.E">B.E</option>
                        <option value="M.Tech">M.Tech</option>
                        <option value="M.E">M.E</option>
                        <option value="B.Sc">B.Sc</option>
                        <option value="M.Sc">M.Sc</option>
                        <option value="BCA">BCA</option>
                        <option value="MCA">MCA</option>
                        <option value="BBA">BBA</option>
                        <option value="MBA">MBA</option>
                        <option value="B.Com">B.Com</option>
                        <option value="M.Com">M.Com</option>
                        <option value="BA">BA</option>
                        <option value="MA">MA</option>
                        <option value="PhD">PhD</option>
                        <option value="Diploma">Diploma</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Field of Study</label>
                      <select
                        value={edu.field || ''}
                        onChange={(e) => handleArrayItemChange('education', index, 'field', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select field</option>
                        <option value="Computer Science">Computer Science</option>
                        <option value="Information Technology">Information Technology</option>
                        <option value="Electronics & Communication">Electronics & Communication</option>
                        <option value="Electrical Engineering">Electrical Engineering</option>
                        <option value="Mechanical Engineering">Mechanical Engineering</option>
                        <option value="Civil Engineering">Civil Engineering</option>
                        <option value="Chemical Engineering">Chemical Engineering</option>
                        <option value="Data Science">Data Science</option>
                        <option value="Artificial Intelligence">Artificial Intelligence</option>
                        <option value="Business Administration">Business Administration</option>
                        <option value="Finance">Finance</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Human Resources">Human Resources</option>
                        <option value="Commerce">Commerce</option>
                        <option value="Mathematics">Mathematics</option>
                        <option value="Physics">Physics</option>
                        <option value="Chemistry">Chemistry</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="month"
                          value={edu.start_year || ''}
                          onChange={(e) => handleArrayItemChange('education', index, 'start_year', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="month"
                          value={edu.end_year || ''}
                          onChange={(e) => handleArrayItemChange('education', index, 'end_year', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={edu.description || ''}
                        onChange={(e) => handleArrayItemChange('education', index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Work Experience Section */}
          {activeSection === 'work' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Work Experience</h2>
                <button
                  onClick={() => addArrayItem('workExperience', {
                    company: '',
                    position: '',
                    start_date: '',
                    end_date: '',
                    description: ''
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Icon name="add" size={18} />
                  Add Experience
                </button>
              </div>

              {formData.workExperience.map((work, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-lg relative">
                  {formData.workExperience.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('workExperience', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                      <input
                        type="text"
                        value={work.company || ''}
                        onChange={(e) => handleArrayItemChange('workExperience', index, 'company', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                      <input
                        type="text"
                        value={work.position || ''}
                        onChange={(e) => handleArrayItemChange('workExperience', index, 'position', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="month"
                        value={work.start_date || ''}
                        onChange={(e) => handleArrayItemChange('workExperience', index, 'start_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="month"
                        value={work.end_date || ''}
                        onChange={(e) => handleArrayItemChange('workExperience', index, 'end_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={work.description || ''}
                        onChange={(e) => handleArrayItemChange('workExperience', index, 'description', e.target.value)}
                        rows={4}
                        placeholder="Describe your role and responsibilities..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Projects Section */}
          {activeSection === 'projects' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
                <button
                  onClick={() => addArrayItem('projects', {
                    title: '',
                    description: '',
                    technologies: '',
                    link: ''
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Icon name="add" size={18} />
                  Add Project
                </button>
              </div>

              {formData.projects.map((project, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-lg relative">
                  {formData.projects.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('projects', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Project Title</label>
                      <input
                        type="text"
                        value={project.title || ''}
                        onChange={(e) => handleArrayItemChange('projects', index, 'title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={project.description || ''}
                        onChange={(e) => handleArrayItemChange('projects', index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technologies</label>
                      <input
                        type="text"
                        value={project.technologies || ''}
                        onChange={(e) => handleArrayItemChange('projects', index, 'technologies', e.target.value)}
                        placeholder="e.g., React, Node.js, MongoDB"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Project Link</label>
                      <input
                        type="url"
                        value={project.link || ''}
                        onChange={(e) => handleArrayItemChange('projects', index, 'link', e.target.value)}
                        placeholder="https://..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Certifications Section */}
          {activeSection === 'certifications' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Certifications</h2>
                <button
                  onClick={() => addArrayItem('certifications', {
                    name: '',
                    issuer: '',
                    date: '',
                    credential_id: '',
                    link: ''
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Icon name="add" size={18} />
                  Add Certification
                </button>
              </div>

              {formData.certifications.map((cert, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-lg relative">
                  {formData.certifications.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('certifications', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Certification Name</label>
                      <input
                        type="text"
                        value={cert.name || ''}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Issuing Organization</label>
                      <input
                        type="text"
                        value={cert.issuer || ''}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'issuer', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Issue Date</label>
                      <input
                        type="month"
                        value={cert.date || ''}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Credential ID</label>
                      <input
                        type="text"
                        value={cert.credential_id || ''}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'credential_id', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Link</label>
                      <input
                        type="url"
                        value={cert.link || ''}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'link', e.target.value)}
                        placeholder="https://..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateProfileEditPage;
